/* ============================================================
   QuoteDisplay — price breakdown card after quote is generated
   ============================================================ */

import type { QuoteResponse, ModelFileResponse } from '../types';
import { Model3DPreview } from './Model3DPreview';

interface QuoteDisplayProps {
  quote: QuoteResponse;
  modelFile: ModelFileResponse;
}

/* TASK-196: the API may deliver numeric enums / nulls (e.g. seller-quote
   responses) — funnel every displayed number through a guard so rendering
   can never crash on an unexpected shape. */
function num(value: number | null | undefined): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatRub(value: number | null | undefined): string {
  return num(value).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function formatMinutes(minutes: number | null | undefined): string {
  const m0 = num(minutes);
  if (m0 < 60) return `${m0} мин`;
  const h = Math.floor(m0 / 60);
  const m = Math.round(m0 % 60);
  return m > 0 ? `${h} ч ${m} мин` : `${h} ч`;
}

function formatMm(mm: number | null | undefined): string {
  const v = num(mm);
  if (v < 10) return v.toFixed(1);
  return Math.round(v).toString();
}

export function QuoteDisplay({ quote, modelFile }: QuoteDisplayProps) {
  const dimensions = `${formatMm(modelFile.boundingBoxX)} × ${formatMm(modelFile.boundingBoxY)} × ${formatMm(modelFile.boundingBoxZ)} мм`;
  const volumeCm3 = (num(modelFile.volumeMm3) / 1000).toFixed(1);

  return (
    <div
      className="card"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-md)',
      }}
    >
      {/* Total price — hero number */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          paddingBottom: 'var(--space-md)',
          borderBottom: '1px solid var(--color-separator)',
        }}
      >
        <span style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>
          Стоимость печати
        </span>
        <span
          style={{
            fontSize: 'var(--fs-h1)',
            fontWeight: 'var(--fw-bold)',
            color: 'var(--color-primary)',
            fontFamily: 'var(--font-numbers)',
          }}
        >
          {formatRub(quote.totalPrice)} ₽
        </span>
      </div>

      {/* cc4909b4: interactive 3D preview of the uploaded model (three.js,
          raw mesh via /models/{id}/content); the ME-2 PNG stays the fallback
          for STEP / no-WebGL / load failures. */}
      <Model3DPreview
        fileId={modelFile.id}
        format={modelFile.format}
        previewUrl={modelFile.previewUrl}
        fileName={modelFile.originalFileName}
        alt={`Модель ${modelFile.originalFileName}`}
        height={240}
      />

      {/* Cost breakdown */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
        <CostRow label="Материал" value={quote.materialCost} hint={`${num(quote.estimatedMaterialGrams).toFixed(0)} г`} />
        <CostRow label="Работа принтера" value={quote.machineCost} hint={formatMinutes(quote.estimatedPrintMinutes)} />
        <CostRow label="Подготовка" value={quote.setupFee} />
        {quote.postProcessingCost > 0 && (
          <CostRow label="Постобработка" value={quote.postProcessingCost} />
        )}
        <CostRow label="Сервисный сбор" value={quote.platformFee} />
      </div>

      {/* Model info */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--space-md)',
          flexWrap: 'wrap',
          paddingTop: 'var(--space-md)',
          borderTop: '1px solid var(--color-separator)',
        }}
      >
        <ModelInfo label="Габариты" value={dimensions} />
        <ModelInfo label="Объём" value={`${volumeCm3} см³`} />
        <ModelInfo
          label="Модель"
          value={modelFile.isManifold ? 'Замкнутая ✓' : 'Незамкнутая ⚠'}
          warn={!modelFile.isManifold}
        />
      </div>

      {/* 3830fe0d: no order CTA here — the upload/quote page only uploads and
          calculates; ordering goes through the cart (Artem 2026-08-28 P2). */}
    </div>
  );
}

function CostRow({ label, value, hint }: { label: string; value: number | null | undefined; hint?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <span style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)' }}>
        {label}
        {hint && (
          <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-tertiary)', marginLeft: 'var(--space-xs)' }}>
            ({hint})
          </span>
        )}
      </span>
      <span
        style={{
          fontSize: 'var(--fs-body)',
          fontWeight: 'var(--fw-medium)',
          fontFamily: 'var(--font-numbers)',
        }}
      >
        {formatRub(value)} ₽
      </span>
    </div>
  );
}

function ModelInfo({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <span style={{ fontSize: 'var(--fs-overline)', color: 'var(--color-text-tertiary)' }}>
        {label.toUpperCase()}
      </span>
      <span
        style={{
          fontSize: 'var(--fs-caption)',
          fontWeight: 'var(--fw-medium)',
          color: warn ? 'var(--color-danger)' : 'var(--color-text-primary)',
        }}
      >
        {value}
      </span>
    </div>
  );
}
